import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaClipboardList,
  FaCreditCard,
  FaExclamationTriangle,
  FaSave,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { useUser } from "./../../../../../pages/Home/UserContext";

import { getGroupsHome } from "./../../../../../services/groupServices";
import {
  createReglementInscription,
  getReglementInscriptionByIdAndSaison,
} from "./../../../../../services/reglementinscriptionServices";

const CURRENT_YEAR = new Date().getFullYear();

const DEFAULT_FORM = {
  saison: getCurrentSeason(),
  montant_lic_assurance: 0,
  montant_cotisation: 0,
  reduction_foyer: 0,
  pass_sport: 0,
  reduction_saison: 0,
  reduction_passplus: 0,
  reduction_labaz: 0,
  type_reglement: "En ligne",
  nb_reglement: 1,
  statut_reglement: "Non réglé",
  observations: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-[#013E8E] focus:ring-4 focus:ring-[#013E8E]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";

function getCurrentSeason() {
  const now = new Date();
  const year = now.getFullYear();
  const seasonStart = new Date(`${year}-08-01T00:00:00`);

  if (now >= seasonStart) {
    return `${year}-${year + 1}`;
  }

  return `${year - 1}-${year}`;
}

function normalize(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function toNumber(value) {
  const number = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(number) ? number : 0;
}

function toMoneyPayload(value) {
  return Number(toNumber(value).toFixed(2));
}

function toReductionPayload(value) {
  const amount = toMoneyPayload(value);

  if (amount === 0) return 0;

  return amount > 0 ? -amount : amount;
}

function formatEUR(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
}

function formatDateFr(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return new Date().toLocaleDateString("fr-FR");
  }

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getUserFullName(user) {
  const prenom = String(user?.prenom || "").trim();
  const nom = String(user?.nom || "").trim().toUpperCase();

  return `${prenom} ${nom}`.trim() || "un administrateur";
}

function getAdherentId(adherent) {
  return adherent?.id || adherent?.adherent_id || adherent?.adherentId || null;
}

function getUserId(adherent) {
  return (
    adherent?.user_id ||
    adherent?.userId ||
    adherent?.user?.user_id ||
    adherent?.User?.user_id ||
    null
  );
}

function getDisplayName(adherent) {
  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

function getMemberGroups(adherent) {
  if (Array.isArray(adherent?.Groups)) {
    return adherent.Groups.map((group) => ({
      id: group?.id,
      nom: group?.nom || group?.name,
    })).filter((group) => group.nom);
  }

  if (Array.isArray(adherent?.groups)) {
    return adherent.groups
      .map((group) => ({
        id: group?.id,
        nom: group?.nom || group?.name,
      }))
      .filter((group) => group.nom);
  }

  if (adherent?.group_name) return [{ nom: adherent.group_name }];
  if (adherent?.groupe_nom) return [{ nom: adherent.groupe_nom }];

  return [];
}

function findSuggestedGroup(adherent, groupsHome) {
  const memberGroups = getMemberGroups(adherent);

  if (!memberGroups.length || !Array.isArray(groupsHome)) return null;

  const memberGroupWithId = memberGroups.find((group) => group.id);

  if (memberGroupWithId) {
    const byId = groupsHome.find(
      (group) => Number(group.id) === Number(memberGroupWithId.id),
    );

    if (byId) return byId;
  }

  for (const memberGroup of memberGroups) {
    const byName = groupsHome.find(
      (group) => normalize(group.nom) === normalize(memberGroup.nom),
    );

    if (byName) return byName;
  }

  return null;
}

function buildObservation(group, user) {
  const base = `Règlement ajouté par ${getUserFullName(user)} le ${formatDateFr()}.`;

  if (!group) return base;

  return `${base} Groupe source : ${group.nom}.`;
}

function getTotal(form) {
  const licence = toNumber(form.montant_lic_assurance);
  const cotisation = toNumber(form.montant_cotisation);

  const reductions =
    Math.abs(toNumber(form.reduction_foyer)) +
    Math.abs(toNumber(form.pass_sport)) +
    Math.abs(toNumber(form.reduction_saison)) +
    Math.abs(toNumber(form.reduction_passplus)) +
    Math.abs(toNumber(form.reduction_labaz));

  return Math.max(0, licence + cotisation - reductions);
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <div className="mt-1">{children}</div>

      {hint ? (
        <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export default function ReglementInscriptionDrawer({
  isOpen,
  onClose,
  adherent,
  onCreated,
}) {
  const { user } = useUser();

  const [groupsHome, setGroupsHome] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);

  const [existingReglement, setExistingReglement] = useState(null);

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const adherentId = getAdherentId(adherent);
  const userId = getUserId(adherent);
  const name = getDisplayName(adherent);

  const memberGroups = useMemo(() => getMemberGroups(adherent), [adherent]);

  const selectedGroup = useMemo(() => {
    return groupsHome.find(
      (group) => Number(group.id) === Number(selectedGroupId),
    );
  }, [groupsHome, selectedGroupId]);

  const total = useMemo(() => getTotal(form), [form]);

  const updateForm = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const checkExistingReglement = useCallback(
    async (targetSaison) => {
      if (!adherentId || !targetSaison) return null;

      try {
        setChecking(true);

        const reglement = await getReglementInscriptionByIdAndSaison(
          adherentId,
          targetSaison,
        );

        setExistingReglement(reglement || null);
        return reglement || null;
      } finally {
        setChecking(false);
      }
    },
    [adherentId],
  );

  useEffect(() => {
    if (!isOpen || !adherent) return;

    let isMounted = true;

    const initDrawer = async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        setExistingReglement(null);
        setSelectedGroupId("");

        const initialSeason = getCurrentSeason();

        setForm({
          ...DEFAULT_FORM,
          saison: initialSeason,
          observations: buildObservation(null, user),
        });

        let groupsResponse = [];
        let existing = null;

        try {
          groupsResponse = await getGroupsHome();
        } catch (error) {
          console.error("Erreur chargement groupes :", error);
          groupsResponse = [];
        }

        try {
          existing = await getReglementInscriptionByIdAndSaison(
            adherentId,
            initialSeason,
          );
        } catch (error) {
          console.warn(
            "Aucun règlement existant trouvé pour cet adhérent et cette saison.",
            error,
          );
          existing = null;
        }

        if (!isMounted) return;

        const groups = Array.isArray(groupsResponse) ? groupsResponse : [];

        setGroupsHome(groups);
        setExistingReglement(existing || null);

        const suggested = findSuggestedGroup(adherent, groups);

        if (suggested) {
          setSelectedGroupId(String(suggested.id));

          setForm((prev) => ({
            ...prev,
            montant_lic_assurance: toMoneyPayload(suggested.tarif_licence),
            montant_cotisation: toMoneyPayload(suggested.tarif_cotisation),
            observations: buildObservation(suggested, user),
          }));
        }
      } catch (error) {
        console.error("Erreur initialisation drawer règlement :", error);
        setErrorMsg(
          "Impossible de préparer le règlement d'inscription pour cet adhérent.",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initDrawer();

    return () => {
      isMounted = false;
    };
  }, [adherent, adherentId, isOpen, user]);

  useEffect(() => {
    if (!isOpen || !adherentId || !form.saison) return;

    const timer = window.setTimeout(() => {
      checkExistingReglement(form.saison);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [adherentId, checkExistingReglement, form.saison, isOpen]);

  const handleGroupChange = (event) => {
    const value = event.target.value;

    setSelectedGroupId(value);

    const group = groupsHome.find((item) => Number(item.id) === Number(value));

    if (!group) {
      updateForm({
        observations: buildObservation(null, user),
      });
      return;
    }

    updateForm({
      montant_lic_assurance: toMoneyPayload(group.tarif_licence),
      montant_cotisation: toMoneyPayload(group.tarif_cotisation),
      observations: buildObservation(group, user),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!adherentId) {
      toast.error("Impossible d'identifier l'adhérent.");
      return;
    }

    if (existingReglement) {
      toast.info("Un règlement existe déjà pour cet adhérent et cette saison.");
      return;
    }

    if (!form.saison?.trim()) {
      toast.error("La saison est obligatoire.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        adherent_id: Number(adherentId),
        saison: form.saison.trim(),
        montant_lic_assurance: toMoneyPayload(form.montant_lic_assurance),
        montant_cotisation: toMoneyPayload(form.montant_cotisation),
        reduction_foyer: toReductionPayload(form.reduction_foyer),
        pass_sport: toReductionPayload(form.pass_sport),
        reduction_saison: toReductionPayload(form.reduction_saison),
        reduction_passplus: toReductionPayload(form.reduction_passplus),
        reduction_labaz: toReductionPayload(form.reduction_labaz),
        type_reglement: form.type_reglement || "En ligne",
        nb_reglement: Number(form.nb_reglement) || 1,
        statut_reglement: form.statut_reglement || "Non réglé",
        observations:
          form.observations?.trim() || buildObservation(selectedGroup, user),
      };

      const created = await createReglementInscription(payload);

      if (!created) {
        toast.error("Le règlement n'a pas pu être créé.");
        return;
      }

      setExistingReglement(created);

      toast.success("Règlement d'inscription créé.");

      onCreated?.(created);
    } catch (error) {
      console.error("Erreur création règlement inscription :", error);
      toast.error("Impossible de créer le règlement d'inscription.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="border-b border-slate-200 px-5 py-4 dark:border-slate-800 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Nouveau règlement
              </p>

              <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                Préparer l'inscription financière
              </h2>

              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Création d'un règlement d'inscription à partir du groupe choisi
                par l'adhérent.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-white"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="mb-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-[#013E8E] shadow-sm dark:bg-slate-950 dark:text-indigo-300">
                <FaUser />
              </span>

              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {name}
                </h3>

                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Adhérent #{adherentId || "—"}
                  {userId ? ` · Utilisateur #${userId}` : ""}
                </p>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {memberGroups.length > 0 ? (
                    memberGroups.map((group, index) => (
                      <span
                        key={`${group.id || group.nom}-${index}`}
                        className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700"
                      >
                        {group.nom}
                      </span>
                    ))
                  ) : (
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-700">
                      Aucun groupe détecté
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {errorMsg ? (
            <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              {errorMsg}
            </div>
          ) : null}

          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="h-20 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-900"
                />
              ))}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {existingReglement ? (
                <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-4 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="mt-1 shrink-0" />

                    <div>
                      <p className="font-bold">
                        Un règlement existe déjà pour cette saison.
                      </p>

                      <p className="mt-1 text-sm leading-6">
                        Règlement #{existingReglement.id} · statut :{" "}
                        {existingReglement.statut_reglement || "—"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : checking ? (
                <div className="rounded-[24px] border border-indigo-200 bg-indigo-50 p-4 text-sm font-bold text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
                  Vérification du règlement existant...
                </div>
              ) : (
                <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                  <div className="flex items-start gap-3">
                    <FaExclamationTriangle className="mt-1 shrink-0" />

                    <div>
                      <p className="font-bold">
                        Aucun règlement trouvé pour cette saison.
                      </p>

                      <p className="mt-1 text-sm leading-6">
                        Tu peux créer le règlement d'inscription avec les tarifs
                        proposés depuis le groupe.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <section className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-[#013E8E] dark:bg-indigo-500/10 dark:text-indigo-300">
                    <FaClipboardList />
                  </span>

                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">
                      Proposition de règlement
                    </h3>

                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Les montants restent modifiables avant validation.
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Saison">
                    <input
                      value={form.saison}
                      onChange={(event) =>
                        updateForm({ saison: event.target.value })
                      }
                      className={inputClass}
                      placeholder={`${CURRENT_YEAR}-${CURRENT_YEAR + 1}`}
                    />
                  </Field>

                  <Field label="Groupe source">
                    <select
                      value={selectedGroupId}
                      onChange={handleGroupChange}
                      className={inputClass}
                    >
                      <option value="">Sélectionner un groupe</option>

                      {groupsHome.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.nom} · licence {group.tarif_licence || 0} € ·
                          cotisation {group.tarif_cotisation || 0} €
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Licence / assurance">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.montant_lic_assurance}
                      onChange={(event) =>
                        updateForm({
                          montant_lic_assurance: event.target.value,
                        })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Cotisation">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.montant_cotisation}
                      onChange={(event) =>
                        updateForm({ montant_cotisation: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Réduction foyer">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.reduction_foyer}
                      onChange={(event) =>
                        updateForm({ reduction_foyer: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Pass Sport">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.pass_sport}
                      onChange={(event) =>
                        updateForm({ pass_sport: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Réduction saison">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.reduction_saison}
                      onChange={(event) =>
                        updateForm({ reduction_saison: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Réduction Pass+">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.reduction_passplus}
                      onChange={(event) =>
                        updateForm({ reduction_passplus: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Réduction Labaz">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.reduction_labaz}
                      onChange={(event) =>
                        updateForm({ reduction_labaz: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Nombre de règlements">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.nb_reglement}
                      onChange={(event) =>
                        updateForm({ nb_reglement: event.target.value })
                      }
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Type de règlement">
                    <select
                      value={form.type_reglement}
                      onChange={(event) =>
                        updateForm({ type_reglement: event.target.value })
                      }
                      className={inputClass}
                    >
                      <option value="En ligne">En ligne</option>
                      <option value="Carte bancaire">Carte bancaire</option>
                      <option value="Chèque">Chèque</option>
                      <option value="Espèces">Espèces</option>
                      <option value="Virement">Virement</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </Field>

                  <Field label="Statut">
                    <select
                      value={form.statut_reglement}
                      onChange={(event) =>
                        updateForm({ statut_reglement: event.target.value })
                      }
                      className={inputClass}
                    >
                      <option value="Non réglé">Non réglé</option>
                      <option value="En cours">En cours</option>
                      <option value="Partiellement réglé">
                        Partiellement réglé
                      </option>
                      <option value="Réglé">Réglé</option>
                      <option value="Annulé">Annulé</option>
                      <option value="Problème">Problème</option>
                    </select>
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="Observations">
                    <textarea
                      value={form.observations}
                      onChange={(event) =>
                        updateForm({ observations: event.target.value })
                      }
                      rows={4}
                      className={`${inputClass} resize-none`}
                    />
                  </Field>
                </div>

                <div className="mt-5 rounded-3xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Total proposé
                      </p>

                      <p className="mt-1 text-3xl font-bold text-slate-900 dark:text-white">
                        {formatEUR(total)}
                      </p>
                    </div>

                    {selectedGroup ? (
                      <div className="text-sm text-slate-600 dark:text-slate-300 sm:text-right">
                        <p className="font-bold text-slate-900 dark:text-white">
                          {selectedGroup.nom}
                        </p>

                        <p>
                          Licence : {formatEUR(selectedGroup.tarif_licence)} ·
                          Cotisation :{" "}
                          {formatEUR(selectedGroup.tarif_cotisation)}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/60">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
                      <FaCreditCard />
                    </span>

                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        Paiement utilisateur
                      </h3>

                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        Pour envoyer une demande de paiement licence-cotisation
                        à l'utilisateur, utilisez la page dédiée aux paiements.
                      </p>
                    </div>
                  </div>

                  <a
                    href="/dashboard/paiements"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
                  >
                    <FaCreditCard />
                    Ouvrir les paiements
                  </a>
                </div>
              </section>
            </form>
          )}
        </div>

        <footer className="border-t border-slate-200 px-5 py-4 dark:border-slate-800 sm:px-6">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || saving || checking || !!existingReglement}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaSave />
              {saving ? "Création..." : "Créer le règlement"}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}